import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    Shield, Mail, DollarSign, Loader2, Eye, Zap, LogOut, Settings,
    ArrowRight, Check, X, AlertCircle, Users, Calendar, Tag, DoorOpen, Ghost, Hash, Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExecutiveDashboard() {
    const { user, logout } = useAuth();
    const [emails, setEmails] = useState([]);
    const [stats, setStats] = useState(null);
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [view, setView] = useState("inbox");
    const [threads, setThreads] = useState([]);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [actionLoading, setActionLoading] = useState(null);
    const [slackStatus, setSlackStatus] = useState(null);
    const [slackChannels, setSlackChannels] = useState([]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, policyRes] = await Promise.all([
                API.get("/executive/stats", { withCredentials: true }),
                API.get("/executive/policy", { withCredentials: true }),
            ]);
            setStats(statsRes.data);
            setPolicy(policyRes.data);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    useEffect(() => {
        API.get("/slack/status", { withCredentials: true }).then(r => setSlackStatus(r.data)).catch(() => {});
    }, []);

    const scanInbox = async () => {
        setScanning(true);
        try {
            const res = await API.get("/executive/inbox", { withCredentials: true });
            setEmails(res.data.emails || []);
            toast.success(`Scanned ${res.data.total} emails. ${res.data.sales_detected} sales detected.`);
        } catch (err) {
            if (err.response?.data?.detail === "Outlook not connected") {
                toast.error("Connect your Outlook account first");
            } else {
                toast.error("Scan failed");
            }
        }
        setScanning(false);
    };

    const fetchThreads = async () => {
        try {
            const res = await API.get("/outlook/threads", { withCredentials: true });
            setThreads(res.data.threads || []);
        } catch { toast.error("Could not load threads"); }
    };

    const fetchCalendar = async () => {
        try {
            const res = await API.get("/outlook/calendar/events", { withCredentials: true });
            setCalendarEvents(res.data.events || []);
        } catch { toast.error("Could not load calendar"); }
    };

    const leaveThread = async (threadId, mode) => {
        setActionLoading(threadId);
        try {
            const res = await API.post(`/outlook/threads/${threadId}/leave`, { mode }, { withCredentials: true });
            toast.success(res.data.message);
            fetchThreads();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
        setActionLoading(null);
    };

    const declineEvent = async (eventId) => {
        setActionLoading(eventId);
        try {
            const res = await API.post(`/outlook/calendar/events/${eventId}/decline`, {}, { withCredentials: true });
            toast.success(res.data.message);
            fetchCalendar();
        } catch { toast.error("Failed to decline"); }
        setActionLoading(null);
    };

    useEffect(() => {
        if (view === "threads" && user?.outlook_connected) fetchThreads();
        if (view === "calendar" && user?.outlook_connected) fetchCalendar();
        if (view === "slack" && slackStatus?.connected) fetchSlackChannels();
    }, [view, user, slackStatus]);

    const fetchSlackChannels = async () => {
        try {
            const res = await API.get("/slack/channels", { withCredentials: true });
            setSlackChannels(res.data.channels || []);
        } catch { toast.error("Could not load Slack channels"); }
    };

    const leaveSlackChannel = async (channelId, mode) => {
        setActionLoading(channelId);
        try {
            const res = await API.post(`/slack/channels/${channelId}/leave`, { mode }, { withCredentials: true });
            toast.success(res.data.message);
            fetchSlackChannels();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
        setActionLoading(null);
    };

    const connectSlack = () => {
        const origin = window.location.origin;
        window.location.href = `${BACKEND_URL}/api/auth/slack?origin=${encodeURIComponent(origin)}`;
    };

    const connectOutlook = () => {
        const origin = window.location.origin;
        window.location.href = `${BACKEND_URL}/api/auth/outlook?origin=${encodeURIComponent(origin)}`;
    };

    const classificationColors = {
        prospecting: "bg-red-500/20 text-red-400 border-red-500/30",
        follow_up: "bg-orange-500/20 text-orange-400 border-orange-500/30",
        cold_outreach: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        legitimate: "bg-green-500/20 text-green-400 border-green-500/30",
        personal: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        unclassified: "bg-white/10 text-[#9CA3AF] border-white/10",
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#047857]" /></div>;

    return (
        <div className="min-h-screen-safe bg-[#050505]">
            <header className="border-b border-white/5 sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-[#047857]" />
                        <span className="font-serif italic text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Inbox Shield</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
                        <span className="text-sm text-[#9CA3AF] hidden sm:inline">{user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-8 space-y-6">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
                        <StatCard icon={Mail} label="Emails Classified" value={stats.total_classified} />
                        <StatCard icon={AlertCircle} label="Sales Detected" value={stats.sales_detected} accent="red" />
                        <StatCard icon={Zap} label="Auto-Replies Sent" value={stats.auto_replies_sent} />
                        <StatCard icon={DollarSign} label="Earned" value={`$${stats.total_earned}`} accent="gold" />
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                    {!user?.outlook_connected ? (
                        <Button onClick={connectOutlook} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="connect-outlook-btn">
                            <Mail className="h-4 w-4 mr-2" />Connect Outlook
                        </Button>
                    ) : (
                        <>
                            <Button onClick={scanInbox} disabled={scanning} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="scan-inbox-btn">
                                {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                                Scan Inbox
                            </Button>
                            <Button onClick={() => setView("threads")} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="threads-btn" title="Leave email threads">
                                <DoorOpen className="h-4 w-4 mr-2" />Threads
                            </Button>
                            <Button onClick={() => setView("calendar")} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="calendar-btn" title="Guard your calendar">
                                <Calendar className="h-4 w-4 mr-2" />Calendar
                            </Button>
                        </>
                    )}
                    {slackStatus?.connected ? (
                        <Button onClick={() => setView("slack")} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="slack-btn" title="Leave Slack channels">
                            <Hash className="h-4 w-4 mr-2" />{slackStatus.team_name || "Slack"}
                        </Button>
                    ) : (
                        <Button onClick={connectSlack} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="connect-slack-btn">
                            <Hash className="h-4 w-4 mr-2" />Connect Slack
                        </Button>
                    )}
                    {policy?.handle && (
                        <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] px-3 py-2">
                            irishgoodbye.io/u/{policy.handle}
                        </Badge>
                    )}
                </div>

                {/* View Switcher */}
                {user?.outlook_connected && (
                    <div className="flex gap-2 border-b border-white/5 pb-4">
                        {["inbox", "threads", "calendar", "slack"].map((v) => (
                            <button key={v} onClick={() => setView(v)}
                                className={`px-4 py-2 text-sm rounded-md transition-colors ${view === v ? "bg-[#047857] text-white" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"}`}
                                data-testid={`view-${v}`}
                            >
                                {v === "inbox" ? "Inbox Shield" : v === "threads" ? "Thread Exit" : v === "calendar" ? "Calendar Guard" : "Slack"}
                            </button>
                        ))}
                    </div>
                )}

                {/* Inbox Shield View */}
                {view === "inbox" && (
                    <>
                        {emails.length > 0 && (
                            <Card className="bg-[#0a0a0a] border-white/5" data-testid="email-list-card">
                                <CardHeader>
                                    <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2">
                                        <Mail className="h-5 w-5 text-[#047857]" />Inbox ({emails.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    {emails.map((e) => (
                                        <div key={e.id} className="flex items-start justify-between p-3 rounded-md bg-black/30 border border-white/5 hover:border-white/10 transition-colors">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="text-sm font-medium truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{e.subject}</p>
                                                    <Badge className={`text-[10px] ${classificationColors[e.classification]}`}>{e.classification}</Badge>
                                                    {e.confidence > 0 && <span className="text-[10px] text-[#6B7280] font-mono">{Math.round(e.confidence * 100)}%</span>}
                                                </div>
                                                <p className="text-xs text-[#6B7280]">{e.sender_name || e.sender}</p>
                                                <p className="text-xs text-[#6B7280]/60 mt-1 truncate">{e.preview}</p>
                                            </div>
                                            {e.is_whitelisted && (
                                                <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400 shrink-0 ml-2">
                                                    <Check className="h-3 w-3 mr-1" />Whitelisted
                                                </Badge>
                                            )}
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                        {emails.length === 0 && user?.outlook_connected && !scanning && (
                            <Card className="bg-[#0a0a0a] border-white/5">
                                <CardContent className="p-12 text-center space-y-4">
                                    <Shield className="h-12 w-12 text-[#047857] mx-auto opacity-50" />
                                    <h3 className="text-lg font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>Your shield is active</h3>
                                    <p className="text-sm text-[#9CA3AF]">Click "Scan Inbox" to classify your recent emails and detect sales outreach.</p>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}

                {/* Thread Exit View */}
                {view === "threads" && (
                    <Card className="bg-[#0a0a0a] border-white/5" data-testid="threads-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2">
                                <DoorOpen className="h-5 w-5 text-[#047857]" />Email Threads ({threads.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {threads.length > 0 ? threads.map((t) => (
                                <div key={t.id} className="flex items-start justify-between p-3 rounded-md bg-black/30 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{t.subject}</p>
                                        <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-1">
                                            <span>{t.from_sender}</span>
                                            <span>{t.message_count} messages</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 ml-3">
                                        <Button size="sm" variant="ghost" onClick={() => leaveThread(t.id, "ghost")} disabled={actionLoading === t.id}
                                            className="text-[#9CA3AF] hover:text-white h-8" title="Ghost — leave silently" data-testid={`ghost-${t.id}`}>
                                            <Ghost className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" onClick={() => leaveThread(t.id, "irish")} disabled={actionLoading === t.id}
                                            className="bg-[#047857] text-white hover:bg-[#036046] h-8 px-3" title="Irish Goodbye — leave with notice" data-testid={`leave-${t.id}`}>
                                            {actionLoading === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><DoorOpen className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Drop Off</span></>}
                                        </Button>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-sm text-[#9CA3AF] text-center py-8">No threads loaded. Make sure Outlook is connected.</p>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Calendar Guard View */}
                {view === "calendar" && (
                    <Card className="bg-[#0a0a0a] border-white/5" data-testid="calendar-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-[#D4AF37]" />Upcoming Events ({calendarEvents.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {calendarEvents.length > 0 ? calendarEvents.map((ev) => (
                                <div key={ev.id} className="flex items-start justify-between p-3 rounded-md bg-black/30 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{ev.summary}</p>
                                        <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-1">
                                            <span>{new Date(ev.start).toLocaleDateString()} {new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            <span>{ev.attendee_count} attendees</span>
                                            <span className="font-mono truncate">{ev.organizer}</span>
                                        </div>
                                    </div>
                                    {ev.response_status !== 'declined' ? (
                                        <Button size="sm" variant="outline" onClick={() => declineEvent(ev.id)} disabled={actionLoading === ev.id}
                                            className="shrink-0 ml-3 h-8 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`decline-${ev.id}`}>
                                            {actionLoading === ev.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Decline"}
                                        </Button>
                                    ) : (
                                        <Badge variant="outline" className="text-[10px] text-[#6B7280] border-[#6B7280]/30 shrink-0 ml-3">Declined</Badge>
                                    )}
                                </div>
                            )) : (
                                <p className="text-sm text-[#9CA3AF] text-center py-8">No upcoming events. Connect Outlook to see your calendar.</p>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Slack View */}
                {view === "slack" && (
                    <Card className="bg-[#0a0a0a] border-white/5" data-testid="slack-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2">
                                <Hash className="h-5 w-5 text-[#047857]" />Slack Channels ({slackChannels.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {!slackStatus?.connected ? (
                                <div className="text-center py-8 space-y-4">
                                    <p className="text-sm text-[#9CA3AF]">Connect Slack to leave channels you no longer need.</p>
                                    <Button onClick={connectSlack} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]">
                                        <Hash className="h-4 w-4 mr-2" />Connect Slack
                                    </Button>
                                </div>
                            ) : slackChannels.length > 0 ? slackChannels.map((ch) => (
                                <div key={ch.id} className="flex items-start justify-between p-3 rounded-md bg-black/30 border border-white/5 hover:border-white/10 transition-colors">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {ch.is_private ? <Lock className="h-3.5 w-3.5 text-[#6B7280]" /> : <Hash className="h-3.5 w-3.5 text-[#6B7280]" />}
                                            <p className="text-sm font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{ch.name}</p>
                                            {ch.num_members > 0 && <Badge variant="outline" className="text-[10px] border-white/10">{ch.num_members} members</Badge>}
                                        </div>
                                        {ch.topic && <p className="text-xs text-[#6B7280] truncate">{ch.topic}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 ml-3">
                                        <Button size="sm" variant="ghost" onClick={() => leaveSlackChannel(ch.id, "ghost")} disabled={actionLoading === ch.id}
                                            className="text-[#9CA3AF] hover:text-white h-8" title="Ghost — leave silently">
                                            <Ghost className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" onClick={() => leaveSlackChannel(ch.id, "irish")} disabled={actionLoading === ch.id}
                                            className="bg-[#047857] text-white hover:bg-[#036046] h-8 px-3" title="Irish Goodbye — leave with notice">
                                            {actionLoading === ch.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><DoorOpen className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Drop Off</span></>}
                                        </Button>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-sm text-[#9CA3AF] text-center py-8">No channels loaded.</p>
                            )}
                        </CardContent>
                    </Card>
                )}
            </main>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent }) {
    const color = accent === "gold" ? "text-[#D4AF37]" : accent === "red" ? "text-red-400" : "text-[#047857]";
    return (
        <Card className="bg-[#0a0a0a] border-white/5">
            <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-4 w-4 ${color}`} />
                    <span className="text-xs text-[#6B7280]">{label}</span>
                </div>
                <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
            </CardContent>
        </Card>
    );
}
